// import { ESessionType } from '@mindverse/accessor-open';
import Icon from '@ant-design/icons';
import type { IQuote } from '@mindverse/accessor-open';
import { Mentions } from 'antd';
import type { MentionsRef } from 'antd/es/mentions';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import type { FocusEventHandler, KeyboardEventHandler, Ref } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import PeopleSvg from '@/components/Icon/svgs/people.svg';
import useMindStorage, { EStorageKey } from '@/components/chat-app/hooks/useMindStorage';
import useGenius from '@/hooks/useGenius';
import { useTranslation } from '@/i18n/client';
import { useCircleChatContext } from '@/ui/CircleChat/CircleChatContext';
import { useHomepageContext } from '@/ui/HomePage/HomepageContext';

import { type FileItem, UpStatus } from '../useUpload';
import styles from './index.less';

// Lazy load FileCard - only loaded when user uploads files
const FileCard = dynamic(() => import('../../fileCard'), {
  ssr: false
});

// import useContact from './useContact';

const autoSize = { maxRows: 5 };

interface IProps {
  inputValue: string;
  inMobile: boolean;
  disabled?: boolean;
  upList: FileItem[];
  isPA: boolean;
  onDelFile(id: string): void; // 可用于标识文件是否可操作（loading、del）
  onPressEnter: KeyboardEventHandler<HTMLTextAreaElement>;
  onInputChange: (text: string) => void;
  onMentionSelect?: (option: { name: string; userId: string }) => void;
  onFocus: FocusEventHandler<HTMLTextAreaElement>;
  onBlur: FocusEventHandler<HTMLTextAreaElement>;
  onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>): void;
  onUploadFiles(fileList: FileList | never[], e?: React.ChangeEvent<HTMLInputElement>): void;
  quote: IQuote | undefined;
  takeoverLoading?: boolean; // 流式接口加载状态
}

function MultimodalInput(props: IProps, ref: Ref<unknown>): JSX.Element {
  const {
    disabled,
    inMobile,
    onDelFile: handleDel,
    isPA,
    upList,
    inputValue,
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    onInputChange: handleInputChange,
    onMentionSelect,
    onPressEnter: handlePressEnter,
    onPaste: handleOnPaste,
    onUploadFiles,
    quote,
    takeoverLoading = false
  } = props;

  const textAreaRef = useRef<MentionsRef>(null);
  const highlightBackdropRef = useRef<HTMLDivElement>(null);
  // const { contactList, handleSearch } = useContact();
  const { homepageData } = useHomepageContext();
  const [sessionInfo] = useMindStorage(EStorageKey.SESSION_INFO);
  const { circleDetail } = useCircleChatContext();

  const detailInfo = useGenius();
  const mindName = detailInfo.mindName || 'Mind';
  const { t } = useTranslation();
  const allMentionText = t('chatApp.input.mentionAll');

  // 存储从 API 获取的成员列表

  // 生成群聊 @ 功能的 options
  // value 使用 "userId::name" 格式，确保每个选项唯一
  // 选择后会插入 @userId::name，然后在 handleInputChange 中替换为 @name
  const mentionOptions = useMemo(() => {
    if (sessionInfo?.members.length === 0) return [];

    const options =
      sessionInfo?.members.map((member) => ({
        // value 使用 userId::name 格式,确保唯一性
        value: `${member.userId}::${member.name}`,
        userId: member.userId,
        name: member.name,
        route: member.route,
        label: (
          <div className={styles.mentionOption}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img alt={member.name} className={styles.mentionAvatar} src={member.avatar} />
            <span className={styles.mentionName}>{member.name}</span>
          </div>
        )
      })) ?? [];

    // 只有管理员才能看到 @All 选项
    const allOption = {
      value: `all::${allMentionText}`,
      userId: 'all',
      name: allMentionText,
      route: '',
      label: (
        <div className={styles.mentionOption}>
          <Icon className={styles.mentionAll} component={PeopleSvg} />
          <span className={styles.mentionName}>{allMentionText}</span>
        </div>
      )
    };

    return [allOption, ...options];
  }, [sessionInfo, allMentionText]);

  // 处理 mention 选择，通知父组件
  const handleMentionSelect = useCallback(
    (option: { value?: string }) => {
      if (!option.value) return;

      // 解析 userId::name 格式
      const [userId, ...nameParts] = option.value.split('::');
      const name = nameParts.join('::'); // 处理名字中可能包含 :: 的情况

      if (userId && name && onMentionSelect) {
        onMentionSelect({ name, userId });
      }
    },
    [onMentionSelect]
  );

  const onFocus = () => {
    textAreaRef.current?.focus();
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    const files = e?.dataTransfer?.files || [];

    onUploadFiles(files);
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
  };

  // 查找所有 mention 的位置
  const findAllMentions = useCallback(() => {
    const mentions: { start: number; end: number; text: string; route?: string }[] = [];

    // 遍历所有成员，查找输入中的 @name
    // 按 name 长度降序排序，避免短名字匹配到长名字的一部分
    const sortedOptions = [...mentionOptions].sort((a, b) => b.name.length - a.name.length);

    sortedOptions.forEach((opt) => {
      // 转义正则特殊字符 - 使用 opt.name 而不是 opt.value
      const escapedName = opt.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // 与 transformMentions 保持一致的正则边界
      const regex = new RegExp(`@${escapedName}(?=\\s|$|[^\\w])`, 'g');
      let match: RegExpExecArray | null;

      while ((match = regex.exec(inputValue)) !== null) {
        const mentionText = match[0];
        // 检查是否与已有 mention 重叠
        const isOverlapping = mentions.some(
          (m) => match!.index < m.end && match!.index + mentionText.length > m.start
        );

        if (!isOverlapping) {
          mentions.push({
            start: match.index,
            end: match.index + mentionText.length,
            text: mentionText,
            route: opt.route
          });
        }
      }
    });

    // 按位置排序
    return mentions.sort((a, b) => a.start - b.start);
  }, [inputValue, mentionOptions]);

  // 处理 @mention 整体操作（删除、左右移动）
  // 以及处理下拉菜单的键盘导航滚动
  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = useCallback(
    (e) => {
      const textarea = e.target as HTMLTextAreaElement;
      const { selectionStart, selectionEnd } = textarea;
      const mentions = findAllMentions();

      // 处理上下键 - 滚动下拉菜单中的选中项到可视区域
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        // 检查下拉菜单是否打开
        const dropdown = document.querySelector('.ant-mentions-dropdown');

        if (dropdown) {
          // 延迟执行，等待 antd 更新选中状态
          setTimeout(() => {
            const activeItem = document.querySelector('.ant-mentions-dropdown-menu-item-active');

            if (activeItem) {
              activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
      }

      // 只有在没有选中文本时才处理
      if (selectionStart !== selectionEnd) return;

      // 处理退格键 - 整体删除
      if (e.key === 'Backspace' && selectionStart > 0) {
        const mentionAtCursor = mentions.find((m) => m.end === selectionStart);

        if (mentionAtCursor) {
          e.preventDefault();
          const newValue =
            inputValue.slice(0, mentionAtCursor.start) + inputValue.slice(selectionStart);

          handleInputChange(newValue);

          setTimeout(() => {
            textarea.setSelectionRange(mentionAtCursor.start, mentionAtCursor.start);
          }, 0);
        }
      }

      // 处理左方向键 - 跳过整个 mention
      if (e.key === 'ArrowLeft' && selectionStart > 0) {
        const mentionAtCursor = mentions.find((m) => m.end === selectionStart);

        if (mentionAtCursor) {
          e.preventDefault();
          textarea.setSelectionRange(mentionAtCursor.start, mentionAtCursor.start);
        }
      }

      // 处理右方向键 - 跳过整个 mention
      if (e.key === 'ArrowRight' && selectionStart < inputValue.length) {
        const mentionAtCursor = mentions.find((m) => m.start === selectionStart);

        if (mentionAtCursor) {
          e.preventDefault();
          textarea.setSelectionRange(mentionAtCursor.end, mentionAtCursor.end);
        }
      }
    },
    [inputValue, handleInputChange, findAllMentions]
  );

  useImperativeHandle(ref, () => {
    return textAreaRef.current;
  });

  useEffect(() => {
    if (quote) {
      onFocus();
    }
  }, [quote]);

  // 渲染带有高亮 mention 的文本
  const renderHighlightedText = useMemo(() => {
    if (!inputValue || mentionOptions.length === 0) return null;

    const mentions = findAllMentions();

    if (mentions.length === 0) return null;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    mentions.forEach((mention, index) => {
      // 添加 mention 之前的普通文本
      if (mention.start > lastIndex) {
        parts.push(
          <span key={`text-${index}`} className={styles.highlightText}>
            {inputValue.slice(lastIndex, mention.start)}
          </span>
        );
      }

      // 添加高亮的 mention
      parts.push(
        <span
          key={`mention-${index}`}
          className={styles.highlightMention}
          data-route={mention.route}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {mention.text}
        </span>
      );
      lastIndex = mention.end;
    });

    // 添加最后一个 mention 之后的文本
    if (lastIndex < inputValue.length) {
      parts.push(
        <span key="text-last" className={styles.highlightText}>
          {inputValue.slice(lastIndex)}
        </span>
      );
    }

    return parts;
  }, [inputValue, mentionOptions, findAllMentions]);

  // 同步 textarea 和 highlightBackdrop 的滚动位置
  useEffect(() => {
    const textarea = textAreaRef.current?.textarea;
    const backdrop = highlightBackdropRef.current;

    if (!textarea || !backdrop) return;

    const handleScroll = () => {
      const scrollTop = textarea.scrollTop;
      const scrollLeft = textarea.scrollLeft;

      // 使用 transform 移动高亮层内容
      const contentDiv = backdrop.firstElementChild as HTMLElement;

      if (contentDiv) {
        contentDiv.style.transform = `translate(${-scrollLeft}px, ${-scrollTop}px)`;
      }
    };

    // 初始同步一次
    handleScroll();

    textarea.addEventListener('scroll', handleScroll);

    // 监听 input 事件
    textarea.addEventListener('input', handleScroll);

    return () => {
      textarea.removeEventListener('scroll', handleScroll);
      textarea.removeEventListener('input', handleScroll);
    };
  }, [renderHighlightedText]); // 只在高亮内容变化时重新绑定

  return (
    <div
      className={classNames(styles.multimodalInput, {
        [styles.mobile]: inMobile,
        [styles.mebot]: !homepageData && !circleDetail
      })}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* 幽灵层，替换mentions真正的输入 */}
      <div
        className={classNames(styles.mentionsWrapper, {
          [styles.hasMention]: !!renderHighlightedText,
          [styles.takeoverLoading]: takeoverLoading
        })}
      >
        {/* 高亮层 - 显示带颜色的 mention */}
        {renderHighlightedText && (
          <div ref={highlightBackdropRef} aria-hidden="true" className={styles.highlightBackdrop}>
            <div>{renderHighlightedText}</div>
          </div>
        )}
        <Mentions
          ref={textAreaRef}
          autoSize={autoSize}
          className={classNames(styles.mentions, {
            [styles.multiline]: upList.length > 0
          })}
          disabled={disabled}
          onBlur={handleOnBlur}
          onChange={handleInputChange}
          onFocus={handleOnFocus}
          onKeyDown={handleKeyDown}
          onPaste={handleOnPaste}
          onPressEnter={handlePressEnter}
          onSelect={handleMentionSelect}
          options={mentionOptions}
          placeholder={isPA ? t('chatApp.input.placeholder') : `Chat with ${mindName} `}
          value={inputValue}
        />
      </div>
      {inputValue.length > 2000 && inputValue.length < 5000 ? (
        <div className={styles.lengthWarning}>
          Input content is quite long. Please be mindful of the length.
        </div>
      ) : null}
      {inputValue.length >= 5000 ? (
        <div className={styles.lengthWarning}>
          Input content has exceeded the limit. Please consider using document upload for input.
        </div>
      ) : null}
      {upList.length ? (
        <div className={styles.fileContainer}>
          {upList.map((file: FileItem) => (
            <FileCard
              key={file.id}
              className={styles.fileCard}
              fileClassName={inMobile ? styles.fileItem : ''}
              id={file.id}
              imgClassName={styles.imgItem}
              link={file.link}
              loading={file.status === UpStatus.LOADING}
              name={file.name}
              onDel={handleDel}
              type={file.type}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default forwardRef(MultimodalInput);
